import { useState } from "react";
import ReferenceType from "$types/ReferenceType";
import { REFERENCE_TYPE_LABELS } from "$utils/typeConstants/ReferenceType";

const ORDER_PLACEHOLDER = "[ORDER_REF]";
const SUBORDERS_PLACEHOLDER = "[SUBORDER_REFS]";
const REF_TYPE_PLACEHOLDER = "[REF_TYPE]";

export const ALL_EQUALS_ADVICE = `Un client a intégré l'intégralité de la commande ${ORDER_PLACEHOLDER} dans une EDB. Nous préconisons un référencement à la réception.`;
export const PARTIAL_RECEPTION_ADVICE = `Lors de la première réception de ${ORDER_PLACEHOLDER}, un référencement ${REF_TYPE_PLACEHOLDER} a été effectué.`;
export const SUBORDER_ADVICES = {
  ALL_PRODUCTS_SAME_QUANTITIES: `Un client a intégré la totalité des produits de la sous-commande ${SUBORDERS_PLACEHOLDER} dans une EDB. Nous préconisons un référencement à la réception.`,
  ALL_PRODUCTS_LESS_QUANTITIES: `Un client a intégré une partie des produits de la sous-commande ${SUBORDERS_PLACEHOLDER} dans une EDB. Nous préconisons un référencement à l’article.`,
  SOME_PRODUCTS_SAME_QUANTITIES: `Un client a intégré une partie des produits de la sous-commande ${SUBORDERS_PLACEHOLDER} dans une EDB. Nous préconisons un référencement à l’article.`,
  SOME_PRODUCTS_LESS_QUANTITIES: `Un client a intégré une partie des produits de la sous-commande ${SUBORDERS_PLACEHOLDER} dans une EDB. Nous préconisons un référencement à l’article.`,
  LESS_QUANTITES_CL_MULTIPLE_SELECTED: `Un client a intégré une partie des produits de(s) sous-commande(s) : ${SUBORDERS_PLACEHOLDER} dans une EDB. Nous préconisons un référencement par sous-commande(s) à l’article.`,
  SAME_QUANTITIES_CL_MULTIPLE_SELECTED: `Un client a intégré la totalité des produits de(s) sous-commande(s) : ${SUBORDERS_PLACEHOLDER} dans une EDB. Nous préconisons un référencement par sous-commande(s).`,
};

export type Advice = {
  text: string;
  orderRef?: string;
  suborderRef?: string;
};

const useAdvices = (defaultValues = []) => {
  const [advices, setAdvices] = useState<Advice[]>(defaultValues);

  const getAdvice = (
    text: string,
    replacers?: {
      orderRef?: string;
      refType?: ReferenceType;
      suborderRefs?: string[];
    },
    suborderRef?: string, // suborder concerned by the advice
    orderRef?: string // order concerned by the advice
  ): Advice => {
    let formatedAdvice: string = text;
    if (replacers.refType) {
      const { refType } = replacers;
      formatedAdvice = formatedAdvice.replace(
        REF_TYPE_PLACEHOLDER,
        REFERENCE_TYPE_LABELS[refType]
      );
    }
    if (replacers.orderRef) {
      const { orderRef } = replacers;
      formatedAdvice = formatedAdvice.replace(ORDER_PLACEHOLDER, orderRef);
    }
    if (replacers.suborderRefs?.length > 0) {
      const { suborderRefs } = replacers;
      const formatter = new Intl.ListFormat("fr"); // "," between list elements except for the latest one => "et"
      formatedAdvice = formatedAdvice.replace(
        SUBORDERS_PLACEHOLDER,
        formatter.format(suborderRefs.sort((r1, r2) => r1.localeCompare(r2)))
      );
    }
    return {
      text: formatedAdvice,
      orderRef,
      suborderRef,
    };
  };

  const add = (advice: Advice) => {
    setAdvices([...advices, advice]);
  };

  const upsert = (advice: Advice) => {
    const idx = advices.findIndex(
      (a) =>
        a.orderRef === advice.orderRef &&
        a.suborderRef === advice.suborderRef &&
        a.text === advice.text
    );
    if (idx !== -1) return; // If advice is the same, exit to prevent infinite loop

    const otherAdvices = advices.filter(
      (a) =>
        a.orderRef !== advice.orderRef || a.suborderRef !== advice.suborderRef
    );
    setAdvices([...otherAdvices, advice]);
  };

  return {
    add,
    getAdvice,
    advices,
    upsert,
  };
};

export default useAdvices;
